class CreateQuoteValidator {
  constructor(formState) {
    this.quoteStatus = formState.quoteStatus;
    this.dateCreated = formState.dateCreated;
    this.createdBy = formState.createdBy;
    this.quoteItems = formState.quoteItems;
    this.quoteTotal = formState.quoteTotal;
    this.subtotal = formState.subtotal;
    this.customerName = formState.customerName;
    this.quoteNum = formState.quoteNum;
    this.expirationDate = formState.expirationDate;
    this.discountPercent = formState.discountPercent;
    this.customerId = formState.customerId;
    this.items = formState.items;

    // Hard errors block the save outright. Soft errors ($0 money values —
    // the warranty/no-charge case) are confirmable: the calling modal shows
    // a confirm dialog and proceeds on approval. Negative and non-numeric
    // money stays hard (non-numeric previously slipped through because
    // NaN <= 0 is false).
    this.errors = [];
    this.softErrors = [];
  }

  async testStatus() {
    if (/active|expired|invoiced/gi.test(this.quoteStatus)) {
      return true;
    }

    this.errors.push("Please select status");
    return false;
  }

  async testDateAndCreator() {
    if (this.dateCreated?.length > 0 && this.createdBy?.length > 0) {
      return true;
    }

    this.errors.push(
      `Something went wrong. Please close the modal and try again.`
    );
    return false;
  }

  async testItems() {
    //   itemName: "",
    //   price: "0",
    //   quantity: "",
    //   discountApplicable: false,

    if (this.items?.length > 0) {
      this.items?.forEach((item, index) => {
        if (item?.itemName === "") {
          this.errors.push(`Please remove and re-select item #${index + 1}`);
          return false;
        }

        if (item?.quantity === "" || parseInt(item?.quantity) <= 0) {
          this.errors.push(
            `Please enter valid quantity for item #${index + 1}`
          );
          return false;
        }

        const nameIsExempt = /(discount|deposit)/i.test(item?.itemName ?? "");

        if (!nameIsExempt) {
          const price = parseInt(item?.price);
          if (isNaN(price) || price < 0) {
            this.errors.push(`Please enter valid price for item #${index + 1}`);
            return false;
          }
          if (price === 0) {
            this.softErrors.push(`Item #${index + 1} is priced at $0.00`);
          }
        }
      });
    } else {
      this.errors.push("Please select item(s)");
      return false;
    }

    return true;
  }

  async testQuoteItems() {
    //   itemName: "",
    //   price: "0",
    //   quantity: "",
    //   discountApplicable: false,

    if (this.quoteItems?.length > 0) {
      this.quoteItems?.forEach((item, index) => {
        if (item?.itemName === "") {
          this.errors.push(`Please remove and re-select item #${index + 1}`);
          return false;
        }

        if (item?.quantity === "" || parseInt(item?.quantity) <= 0) {
          this.errors.push(
            `Please enter valid quantity for item #${index + 1}`
          );
          return false;
        }

        const price = parseInt(item?.price);
        if (isNaN(price) || price < 0) {
          this.errors.push(`Please enter valid price for item #${index + 1}`);
          return false;
        }
        if (price === 0) {
          this.softErrors.push(`Item #${index + 1} is priced at $0.00`);
        }
      });
    } else {
      this.errors.push("Please select item(s)");
      return false;
    }

    return true;
  }

  async testCustomerName() {
    if (this.customerName === "") {
      this.errors.push("Please enter customer name");
      return false;
    }

    return true;
  }

  async testUpdateTotals() {
    const total = parseInt(this.quoteTotal);
    if (isNaN(total) || total < 0) {
      this.errors.push("Invalid total");
      return false;
    }
    if (total === 0) {
      this.softErrors.push("Total is $0.00");
    }

    const subtotal = parseInt(this.subtotal);
    if (isNaN(subtotal) || subtotal < 0) {
      this.errors.push("Invalid subtotal");
      return false;
    }
    if (subtotal === 0) {
      this.softErrors.push("Subtotal is $0.00");
    }

    return true;
  }

  async testTotals() {
    const subtotal = parseInt(this.subtotal);
    if (isNaN(subtotal) || subtotal < 0) {
      this.errors.push("Invalid subtotal");
      return false;
    }
    if (subtotal === 0) {
      this.softErrors.push("Subtotal is $0.00");
    }

    const total = parseInt(this.quoteTotal);
    if (isNaN(total) || total < 0) {
      this.errors.push("Invalid quote total");
      return false;
    }
    if (total === 0) {
      this.softErrors.push("Quote total is $0.00");
    }

    return true;
  }

  async testExpiration() {
    const expire = parseInt(this.expirationDate);
    const created = parseInt(this.dateCreated);

    if (isNaN(expire) || isNaN(created) || expire <= created) {
      this.errors.push("Invalid expiration date");
      return false;
    }

    return true;
  }

  async testDiscount() {
    if (parseInt(this.discountPercent) < 0) {
      this.errors.push("Invalid discount percentage");
      return false;
    }

    return true;
  }

  async testCustomerId() {
    if (this.customerId?.length > 0) {
      return true;
    }

    this.errors.push("Missing Customer Id. Please Contact Support");
    return false;
  }

  // Both entry points return `true` when fully clean, otherwise
  // { hard: string[], soft: string[] }. The two callers (CreateQuoteModal,
  // QuoteInvoiceEditPage) block on hard errors and confirm-then-proceed on
  // soft-only ($0 money) results.
  async updateValid() {
    this.errors = [];
    this.softErrors = [];

    //run tests for errors first
    await this.testItems();
    await this.testUpdateTotals();
    await this.testDiscount();

    if (this.errors?.length === 0 && this.softErrors?.length === 0) {
      return true;
    }

    return { hard: [...this.errors], soft: [...this.softErrors] };
  }

  async createClientQuoteValid() {
    this.errors = [];
    this.softErrors = [];

    //run tests for errors first
    await this.testStatus();
    await this.testQuoteItems();
    await this.testCustomerName();
    await this.testDateAndCreator();
    await this.testTotals();
    await this.testExpiration();
    await this.testDiscount();
    await this.testCustomerId();

    if (this.errors?.length === 0 && this.softErrors?.length === 0) {
      return true;
    }

    return { hard: [...this.errors], soft: [...this.softErrors] };
  }
}

export default CreateQuoteValidator;
